import React, { useContext, useEffect, useMemo, useState } from "react";
import { AppContext } from "../context/AppContext";
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Typography,
  Tag,
  Spin,
  Empty,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DollarOutlined,
  TeamOutlined,
  TransactionOutlined,
} from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const { Title, Text } = Typography;

const ZAMBIAN_COLORS = {
  green: "#078930",
  red: "#EF3340",
  black: "#000000",
  gold: "#FFB81C",
};

const TodaySummary = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [txns, setTxns] = useState([]);
  const [newApps, setNewApps] = useState([]);

  const startOfToday = useMemo(() => moment().startOf("day"), []);
  const endOfToday = useMemo(() => moment().endOf("day"), []);

  const fetchData = async () => {
    if (!company?.id || !token) return;
    setLoading(true);
    try {
      const [txnsRes, loansRes] = await Promise.all([
        axios.get(`https://zedlenders.pickmesms.com/items/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields: "*,loan.*,loan.assigned_to.*",
            filter: {
              company: company.id,
              transaction_date: {
                _between: [
                  startOfToday.toISOString(),
                  endOfToday.toISOString(),
                ],
              },
            },
            sort: ["-date_created", "-transaction_date"],
            limit: -1,
          },
        }),
        axios.get(`https://zedlenders.pickmesms.com/items/loans`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            fields:
              "id,amount,status,assigned_to.first_name,assigned_to.last_name,date_created,date_updated",
            filter: {
              company: company.id,
              date_created: {
                _between: [
                  startOfToday.format("YYYY-MM-DD"),
                  endOfToday.format("YYYY-MM-DD"),
                ],
              },
            },
            limit: -1,
          },
        }),
      ]);

      setTxns(txnsRes.data.data || []);
      setNewApps(loansRes.data.data || []);
    } catch (err) {
      console.error("Failed to load today's summary:", err);
      message.error("Failed to load today's summary data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, token]);

  // Aggregate today’s metrics
  const metrics = useMemo(() => {
    const totalTransactions = txns.length;
    const inflowTypes = new Set([
      "Loan Repayment",
      "Interest Payment",
      "Admin Fees",
      "Float Addition",
    ]);
    const outflowTypes = new Set([
      "Loan Disbursement",
      "Transfer Fees",
      "Operational Cost",
    ]);

    let inflow = 0;
    let outflow = 0;
    let interest = 0;

    txns.forEach((t) => {
      const amount = Number(t.amount || 0);
      if (t.transaction_type === "Interest Payment") interest += amount;
      if (inflowTypes.has(t.transaction_type)) inflow += amount;
      if (outflowTypes.has(t.transaction_type)) outflow += Math.abs(amount);
    });

    return {
      totalTransactions,
      inflow,
      outflow,
      interest,
    };
  }, [txns]);

  // Officer performance for today
  const officerRows = useMemo(() => {
    const byOfficer = new Map();

    const keyOf = (first, last) =>
      `${first || ""} ${last || ""}`.trim() || "Unassigned";

    // New Applications today (by assigned_to)
    newApps.forEach((loan) => {
      const first = loan.assigned_to?.first_name;
      const last = loan.assigned_to?.last_name;
      const key = keyOf(first, last);
      if (!byOfficer.has(key))
        byOfficer.set(key, {
          officer: key,
          newApps: 0,
          disbursedCount: 0,
          disbursedAmount: 0,
          collectionsCount: 0,
          collectionsAmount: 0,
        });
      byOfficer.get(key).newApps += 1;
    });

    // Transactions today grouped by loan.assigned_to
    txns.forEach((t) => {
      const first = t.loan?.assigned_to?.first_name;
      const last = t.loan?.assigned_to?.last_name;
      const key = keyOf(first, last);
      if (!byOfficer.has(key))
        byOfficer.set(key, {
          officer: key,
          newApps: 0,
          disbursedCount: 0,
          disbursedAmount: 0,
          collectionsCount: 0,
          collectionsAmount: 0,
        });
      const row = byOfficer.get(key);
      const amt = Number(t.amount || 0);
      if (t.transaction_type === "Loan Disbursement") {
        row.disbursedCount += 1;
        row.disbursedAmount += Math.abs(amt);
      }
      if (t.transaction_type === "Loan Repayment") {
        row.collectionsCount += 1;
        row.collectionsAmount += amt;
      }
    });

    return Array.from(byOfficer.values()).sort((a, b) =>
      a.officer.localeCompare(b.officer)
    );
  }, [newApps, txns]);

  const columns = [
    {
      title: "Loan Officer",
      dataIndex: "officer",
      render: (name) => (
        <span>
          <TeamOutlined /> {name}
        </span>
      ),
    },
    {
      title: "New Apps",
      dataIndex: "newApps",
    },
    {
      title: "Disbursed (count)",
      dataIndex: "disbursedCount",
    },
    {
      title: "Disbursed (ZMW)",
      dataIndex: "disbursedAmount",
      render: (v) => `ZMW ${Number(v || 0).toLocaleString()}`,
    },
    {
      title: "Collections (count)",
      dataIndex: "collectionsCount",
    },
    {
      title: "Collections (ZMW)",
      dataIndex: "collectionsAmount",
      render: (v) => `ZMW ${Number(v || 0).toLocaleString()}`,
    },
  ];

  if (loading) {
    return (
      <div style={{ minHeight: 200, display: "grid", placeItems: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0, color: ZAMBIAN_COLORS.green }}>
              Today’s Summary
            </Title>
            <Text type="secondary">
              {company?.company_name || company?.name} • {moment().format("dddd, DD MMM YYYY")}
            </Text>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="Transactions Today"
              value={metrics.totalTransactions}
              prefix={<TransactionOutlined />}
              valueStyle={{ color: ZAMBIAN_COLORS.black }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="Total Inflow"
              value={metrics.inflow}
              prefix={<ArrowDownOutlined />}
              suffix="ZMW"
              valueStyle={{ color: ZAMBIAN_COLORS.green }}
              formatter={(v) => Number(v || 0).toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="Total Outflow"
              value={metrics.outflow}
              prefix={<ArrowUpOutlined />}
              suffix="ZMW"
              valueStyle={{ color: ZAMBIAN_COLORS.red }}
              formatter={(v) => Number(v || 0).toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="Interest Collected"
              value={metrics.interest}
              prefix={<DollarOutlined />}
              suffix="ZMW"
              valueStyle={{ color: ZAMBIAN_COLORS.gold }}
              formatter={(v) => Number(v || 0).toLocaleString()}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title="Loan Officer Performance (Today)"
        bordered={false}
        style={{ marginBottom: 16 }}
      >
        {officerRows.length === 0 ? (
          <Empty description="No activity recorded today" />
        ) : (
          <Table
            columns={columns}
            dataSource={officerRows}
            rowKey={(r) => r.officer}
            pagination={{ pageSize: 8 }}
            scroll={{ x: true }}
          />
        )}
      </Card>
    </div>
  );
};

export default TodaySummary;

